import { Flex, Link, Text } from "@hopper-ui/components";
import { UpsellIcon } from "@hopper-ui/icons";
import { type DateFormatters, differenceInDays, getNow, useDateFormatters } from "@sg-protect/components";
import { Trans } from "@sg-protect/federation-runtime/i18n";
import { type ShellTFunction, useShellTranslation } from "../../../i18n/i18next.ts";
import { useSession } from "../../../SessionProvider.tsx";
import { useTrackButtonClicked } from "../../../tracking/useTrackEvent.ts";
import { useSubscriptionBanners } from "../../../useSubscriptionBanners.ts";
import { upsellAction } from "../../../upsellAction.ts";

const DAYS_LEFT_THRESHOLD = 30;

function getExpiryLabel(
    expiryDate: string | null | undefined,
    toLongDate: DateFormatters["toLongDate"],
    t: ShellTFunction
): string | null {
    if (!expiryDate) {
        return null;
    }

    const daysLeft = differenceInDays(expiryDate, getNow());

    if (daysLeft < 0) {
        return t("ReadOnlyBanner.expiry.expired");
    }

    if (daysLeft === 0) {
        return t("ReadOnlyBanner.expiry.today");
    }

    if (daysLeft <= DAYS_LEFT_THRESHOLD) {
        return t("ReadOnlyBanner.expiry.inDays", { count: daysLeft });
    }

    return t("ReadOnlyBanner.expiry.onDate", { date: toLongDate(expiryDate) });
}

export function ReadOnlyBanner() {
    const { t, i18n } = useShellTranslation();
    const trackButtonClicked = useTrackButtonClicked();
    const { isReadOnlyBannerVisible } = useSubscriptionBanners();
    const session = useSession();
    const { toLongDate } = useDateFormatters();
    const expiryDate = session.products?.protect.subscription?.endDate;

    if (!isReadOnlyBannerVisible) {
        return null;
    }

    const handleContactClick = () => {
        trackButtonClicked("contact us", "ReadOnlyBanner");
        upsellAction();
    };

    const expiryLabel = getExpiryLabel(expiryDate, toLongDate, t);

    return (
        <Flex
            alignItems="center"
            gap="inline-sm"
            wrap="wrap"
            backgroundColor="upsell"
            color="upsell"
            padding="inset-squish-md"
        >
            <Flex alignItems="center" gap="inline-sm" wrap="nowrap" grow={1}>
                <UpsellIcon size="sm" />
                <Text size="sm">
                    <Trans
                        i18n={i18n}
                        ns="shell"
                        i18nKey="ReadOnlyBanner.description"
                        components={{
                            contactLink: <Link size="sm" color="inherit" onPress={handleContactClick} />
                        }}
                    />
                </Text>
            </Flex>
            {expiryLabel != null && (
                <Text size="sm" fontWeight="body-sm-semibold" marginLeft="inline-lg">
                    {expiryLabel}
                </Text>
            )}
        </Flex>
    );
}
